import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Generate year options
const generateYears = (fromYear, toYear) => {
  const years = []
  for (let year = toYear; year >= fromYear; year--) {
    years.push(year)
  }
  return years
}

// Month names (short version to prevent overflow)
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
]

/**
 * Enhanced DatePicker with dropdown month/year selection
 * @param {Date} date - Selected date
 * @param {Function} onDateChange - Callback when date changes
 * @param {string} placeholder - Placeholder text
 * @param {number} fromYear - Start year for dropdown (default: 1950)
 * @param {number} toYear - End year for dropdown (default: current year)
 * @param {Date} minDate - Minimum selectable date
 * @param {Date} maxDate - Maximum selectable date
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable the picker
 */
export function DatePicker({ 
  date, 
  onDateChange, 
  placeholder = "Pick a date",
  fromYear = 1950,
  toYear = new Date().getFullYear(),
  minDate,
  maxDate,
  className,
  disabled = false
}) {
  const [open, setOpen] = React.useState(false)
  
  // Initialize viewDate: use selected date, or default to a valid year within range
  const getInitialViewDate = () => {
    if (date) return date
    const now = new Date()
    // If current year is outside the valid range, use middle of range
    if (now.getFullYear() > toYear) {
      return new Date(toYear, 0, 1) // January 1st of toYear
    }
    if (now.getFullYear() < fromYear) {
      return new Date(fromYear, 0, 1)
    }
    return now
  }
  
  const [viewDate, setViewDate] = React.useState(getInitialViewDate)
  
  const years = React.useMemo(() => generateYears(fromYear, toYear), [fromYear, toYear])
  
  const currentMonth = viewDate.getMonth()
  const currentYear = viewDate.getFullYear()
  
  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate()
  }
  
  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay()
  }
  
  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth)
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth)
    const days = []
    
    // Previous month days
    const prevMonthDays = getDaysInMonth(currentYear, currentMonth - 1)
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: new Date(currentYear, currentMonth - 1, prevMonthDays - i)
      })
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(currentYear, currentMonth, i)
      })
    }
    
    // Next month days
    const remainingDays = 42 - days.length
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(currentYear, currentMonth + 1, i)
      })
    }
    
    return days
  }
  
  const calendarDays = generateCalendarDays()
  
  const handleMonthChange = (month) => {
    setViewDate(new Date(currentYear, parseInt(month), 1))
  }
  
  const handleYearChange = (year) => {
    setViewDate(new Date(parseInt(year), currentMonth, 1))
  }
  
  const handlePrevMonth = () => {
    setViewDate(new Date(currentYear, currentMonth - 1, 1))
  }
  
  const handleNextMonth = () => {
    setViewDate(new Date(currentYear, currentMonth + 1, 1))
  }
  
  const handleDateSelect = (selectedDate) => {
    onDateChange(selectedDate)
    setOpen(false)
  }
  
  const isDateDisabled = (checkDate) => {
    if (minDate && checkDate < new Date(minDate.setHours(0, 0, 0, 0))) return true
    if (maxDate && checkDate > new Date(maxDate.setHours(23, 59, 59, 999))) return true
    return false
  }
  
  const isToday = (checkDate) => {
    const today = new Date()
    return checkDate.getDate() === today.getDate() &&
           checkDate.getMonth() === today.getMonth() &&
           checkDate.getFullYear() === today.getFullYear()
  }
  
  const isSelected = (checkDate) => {
    if (!date) return false
    return checkDate.getDate() === date.getDate() &&
           checkDate.getMonth() === date.getMonth() &&
           checkDate.getFullYear() === date.getFullYear()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "dd MMM yyyy") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          {/* Month/Year Navigation */}
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handlePrevMonth}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center gap-1">
              <Select value={currentMonth.toString()} onValueChange={handleMonthChange}>
                <SelectTrigger className="h-8 min-w-[55px] w-auto text-xs px-1.5 gap-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month, index) => (
                    <SelectItem key={month} value={index.toString()}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={currentYear.toString()} onValueChange={handleYearChange}>
                <SelectTrigger className="h-8 min-w-[65px] w-auto text-xs px-1.5 gap-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleNextMonth}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="h-8 w-8 text-center text-xs font-medium text-muted-foreground flex items-center justify-center"
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((dayObj, index) => {
              const isDisabled = isDateDisabled(dayObj.date)
              const selected = isSelected(dayObj.date)
              const today = isToday(dayObj.date)
              
              return (
                <button
                  key={index}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleDateSelect(dayObj.date)}
                  className={cn(
                    "h-8 w-8 text-sm rounded-md transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
                    !dayObj.isCurrentMonth && "text-muted-foreground/50",
                    isDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
                    selected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                    today && !selected && "bg-accent text-accent-foreground font-semibold"
                  )}
                >
                  {dayObj.day}
                </button>
              )
            })}
          </div>
          
          {/* Today Button */}
          <div className="mt-3 pt-3 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => {
                const today = new Date()
                setViewDate(today)
                if (!isDateDisabled(today)) {
                  handleDateSelect(today)
                }
              }}
            >
              Today
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * DatePicker specifically configured for Date of Birth
 */
export function DOBDatePicker({ date, onDateChange, placeholder = "Select date of birth", className, disabled }) {
  const currentYear = new Date().getFullYear()
  const maxDate = new Date() // Can't be born in the future
  maxDate.setFullYear(currentYear - 18) // Must be at least 18
  
  return (
    <DatePicker
      date={date}
      onDateChange={onDateChange}
      placeholder={placeholder}
      fromYear={1940}
      toYear={currentYear - 18}
      maxDate={maxDate}
      className={className}
      disabled={disabled}
    />
  )
}

/**
 * DatePicker for filter scenarios (allows future dates)
 */
export function FilterDatePicker({ date, onDateChange, placeholder = "Select date", className, disabled }) {
  const currentYear = new Date().getFullYear()
  
  return (
    <DatePicker
      date={date}
      onDateChange={onDateChange}
      placeholder={placeholder}
      fromYear={2020}
      toYear={currentYear + 5}
      className={className}
      disabled={disabled}
    />
  )
}

/**
 * DatePicker for future dates (e.g., exam planning)
 */
export function FutureDatePicker({ date, onDateChange, placeholder = "Select date", className, disabled }) {
  const currentYear = new Date().getFullYear()
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  return (
    <DatePicker
      date={date}
      onDateChange={onDateChange}
      placeholder={placeholder}
      fromYear={currentYear}
      toYear={currentYear + 5}
      minDate={today}
      className={className}
      disabled={disabled}
    />
  )
}

/**
 * MonthPicker - Select month and year only (no day selection)
 * @param {string} value - Selected month in "YYYY-MM" format
 * @param {Function} onChange - Callback when month changes (receives "YYYY-MM" format)
 * @param {string} placeholder - Placeholder text
 * @param {number} fromYear - Start year for dropdown
 * @param {number} toYear - End year for dropdown
 * @param {string} minMonth - Minimum selectable month in "YYYY-MM" format
 * @param {string} maxMonth - Maximum selectable month in "YYYY-MM" format
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable the picker
 */
export function MonthPicker({ 
  value, 
  onChange, 
  placeholder = "Select month",
  fromYear = 2015,
  toYear = new Date().getFullYear() + 2,
  minMonth,
  maxMonth,
  className,
  disabled = false
}) {
  const [open, setOpen] = React.useState(false)
  // Track selected year separately to allow two-step selection
  const [tempYear, setTempYear] = React.useState(null)
  
  const years = React.useMemo(() => generateYears(fromYear, toYear), [fromYear, toYear])
  
  // Parse current value
  const selectedYear = value ? parseInt(value.split('-')[0]) : null
  const selectedMonth = value ? parseInt(value.split('-')[1]) - 1 : null
  
  // Initialize tempYear when opening or when value changes
  React.useEffect(() => {
    if (open && selectedYear) {
      setTempYear(selectedYear)
    }
  }, [open, selectedYear])
  
  const handleYearSelect = (year) => {
    setTempYear(parseInt(year))
    // Don't auto-select month - wait for user to click a month
  }
  
  const handleMonthSelect = (month) => {
    const year = tempYear || selectedYear || new Date().getFullYear()
    const monthStr = String(month + 1).padStart(2, '0')
    onChange(`${year}-${monthStr}`)
    setOpen(false)
    setTempYear(null)
  }
  
  const isMonthDisabled = (year, month) => {
    const checkValue = `${year}-${String(month + 1).padStart(2, '0')}`
    if (minMonth && checkValue < minMonth) return true
    if (maxMonth && checkValue > maxMonth) return true
    return false
  }
  
  // Format display value
  const displayValue = value 
    ? `${MONTHS[parseInt(value.split('-')[1]) - 1]} ${value.split('-')[0]}`
    : null
  
  // The year to use for month grid (prefer tempYear for current selection flow)
  const activeYear = tempYear || selectedYear

  return (
    <Popover open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen)
      if (!isOpen) setTempYear(null) // Reset temp year when closing
    }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          {/* Year Selection */}
          <div className="mb-3">
            <Select 
              value={activeYear?.toString() || ''} 
              onValueChange={handleYearSelect}
            >
              <SelectTrigger className="h-9 w-full text-sm">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Instruction text */}
          {activeYear && !selectedMonth && !value && (
            <p className="text-xs text-muted-foreground mb-2 text-center">Now select a month</p>
          )}
          
          {/* Month Grid */}
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((month, index) => {
              const year = activeYear || new Date().getFullYear()
              const isDisabled = isMonthDisabled(year, index)
              const isSelected = selectedYear === activeYear && selectedMonth === index
              
              return (
                <button
                  key={month}
                  type="button"
                  disabled={isDisabled || !activeYear}
                  onClick={() => handleMonthSelect(index)}
                  className={cn(
                    "px-3 py-2 text-sm rounded-md transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
                    isDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
                    !activeYear && "opacity-50 cursor-not-allowed",
                    isSelected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                  )}
                >
                  {month}
                </button>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
